/**
 * Hunter's Lodge mastery panel — exported row-builders for use inside the
 * unified craft-menu tab system (Task 7, 2026-06-19).
 *
 * The standalone `createPetMasteryPanel` wrapper has been retired; the craft
 * menu (craft-menu.ts) now hosts mastery/trophy content as custom tabs when
 * buildingType === "HuntersLodge".
 *
 * Exported builders:
 *   buildPetMasteryRows(send, notify) → { el, refresh(cube) }
 *   buildTrophyRows(send, notify)     → { el, refresh(cube) }
 *
 * Server routes driven here (server is authoritative; client is pre-flight only):
 *   send("pet-upgrade", { upgradeId })
 *     → "pet-upgrade-ok" | "pet-upgrade-rejected" (handled in main.ts)
 *   send("collect-trophy", { trophyType })
 *     → "collect-trophy-ok" | "collect-trophy-rejected" (handled in main.ts)
 *
 * Purchased state: `getMod(cube, "taughtPets")` bitmask.
 * Trophy inventory: scan `cube.backpack` for slots with itemId === trophyType.
 *
 * Pre-flight (server still authoritative; range check lands as a server toast):
 *   - Upgrade already owned → show "Owned", button hidden (CSS .spell-row--owned).
 *   - Can't afford (no material AND insufficient gold) → dim + toast on click.
 *   - Trophy not in backpack → dim (server would reject anyway).
 */
import {
  getMod,
  getMaterial,
  PET_UPGRADES,
  TROPHIES,
  type UpgradeDef,
  type TrophyDef,
  type TrophyType,
  type Cube,
} from "@tribez/shared";

type SendFn = (type: string, payload: unknown) => void;
type NotifyFn = (msg: string) => void;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** True if the given upgrade bit is set in cube.mods["taughtPets"]. */
function isUpgradeOwned(cube: Cube, bit: number): boolean {
  return (getMod(cube, "taughtPets") & (1 << bit)) !== 0;
}

/** True if the cube can pay for this upgrade (material-first, gold fallback).
 *  Routes through getMaterial so leather/bearPelt are read from the backpack
 *  (RESOURCE_POLICY kind:"item"), matching the server canAfford logic. */
function canAffordUpgrade(cube: Cube, u: UpgradeDef): boolean {
  if (getMaterial(cube, u.materialId) >= u.materialQty) return true;
  return getMaterial(cube, "gold") >= u.goldCost;
}

/** Cost label: "1 Leather / 20g" or "2 Cave Bear Pelt / 50g". */
function costLabel(u: UpgradeDef): string {
  const mat = u.materialId === "leather" ? "Leather" : "Cave Bear Pelt";
  return `${u.materialQty} ${mat} / ${u.goldCost}g`;
}

/** True if the player carries at least one of the given trophy type. */
function hasTrophyInPack(cube: Cube, type: TrophyType): boolean {
  let found = false;
  cube.backpack.forEach((slot) => {
    if (slot.itemId === type && slot.count > 0) found = true;
  });
  return found;
}

// ---------------------------------------------------------------------------
// Row ref shapes
// ---------------------------------------------------------------------------

interface UpgradeRowRef {
  el: HTMLDivElement;
  def: UpgradeDef;
}

interface TrophyRowRef {
  el: HTMLDivElement;
  def: TrophyDef;
}

// ---------------------------------------------------------------------------
// Exported reusable builders
// ---------------------------------------------------------------------------

/** Build the Pet Mastery upgrade rows (7 upgrades).
 *  Returns a container element + a refresh(cube) that toggles owned/affordable classes.
 *  The container is a plain <div> — caller appends it wherever it wants. */
export function buildPetMasteryRows(
  send: SendFn,
  notify?: NotifyFn,
): { el: HTMLDivElement; refresh(cube: Cube): void } {
  const container = document.createElement("div");
  container.className = "lodge-mastery-rows";

  const sectionHdr = document.createElement("div");
  sectionHdr.className = "shop-menu__section";
  sectionHdr.textContent = "Pet Mastery Upgrades";
  container.appendChild(sectionHdr);

  const upgradeRows: UpgradeRowRef[] = [];
  let lastCube: Cube | null = null;

  for (const def of PET_UPGRADES) {
    const row = document.createElement("div");
    row.className = "spell-row";
    row.dataset.upgrade = def.id;
    row.innerHTML = `
      <div class="spell-row__main">
        <div class="spell-row__name">${def.label} <span class="spell-row__price">${costLabel(def)}</span></div>
        <div class="spell-row__blurb">${def.blurb}</div>
      </div>
      <div class="spell-row__action">
        <button class="spell-row__buy" type="button">Learn</button>
      </div>
    `;

    const buyBtn = row.querySelector<HTMLButtonElement>(".spell-row__buy")!;
    const upgradeId = def.id;
    const upgradeName = def.label;
    const upgradeDef = def;

    buyBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      if (!lastCube) return;
      if (isUpgradeOwned(lastCube, upgradeDef.bit)) return;
      if (!canAffordUpgrade(lastCube, upgradeDef)) {
        const matName = upgradeDef.materialId === "leather" ? "Leather" : "Cave Bear Pelt";
        const gold = getMaterial(lastCube, "gold");
        if (gold < upgradeDef.goldCost) {
          notify?.(`Need ${upgradeDef.goldCost - gold} more gold for ${upgradeName} (or ${upgradeDef.materialQty} ${matName})`);
        }
        return;
      }
      send("pet-upgrade", { upgradeId });
    });

    container.appendChild(row);
    upgradeRows.push({ el: row, def });
  }

  function refresh(cube: Cube): void {
    lastCube = cube;
    for (const r of upgradeRows) {
      const owned = isUpgradeOwned(cube, r.def.bit);
      r.el.classList.toggle("spell-row--owned", owned);
      const affords = !owned && canAffordUpgrade(cube, r.def);
      r.el.classList.toggle("spell-row--unaffordable", !owned && !affords);
    }
  }

  return { el: container, refresh };
}

/** Build the Trophy deposit rows (3 trophies).
 *  Returns a container element + a refresh(cube) that dims rows when trophy is not in pack. */
export function buildTrophyRows(
  send: SendFn,
  notify?: NotifyFn,
): { el: HTMLDivElement; refresh(cube: Cube): void } {
  const container = document.createElement("div");
  container.className = "lodge-trophy-rows";

  const sectionHdr = document.createElement("div");
  sectionHdr.className = "shop-menu__section";
  sectionHdr.textContent = "Trophy Deposits";
  container.appendChild(sectionHdr);

  const hintEl = document.createElement("div");
  hintEl.className = "shop-menu__hint";
  hintEl.style.marginBottom = "4px";
  hintEl.textContent = "Deposit a trophy carried in your backpack to permanently buff your pets at this lodge.";
  container.appendChild(hintEl);

  const trophyRows: TrophyRowRef[] = [];
  let lastCube: Cube | null = null;

  for (const def of TROPHIES) {
    const row = document.createElement("div");
    row.className = "spell-row";
    row.dataset.trophy = def.type;
    row.innerHTML = `
      <div class="spell-row__main">
        <div class="spell-row__name">${def.label}</div>
        <div class="spell-row__blurb">${def.blurb}</div>
      </div>
      <div class="spell-row__action">
        <button class="spell-row__buy" type="button">Deposit</button>
      </div>
    `;

    const depositBtn = row.querySelector<HTMLButtonElement>(".spell-row__buy")!;
    const trophyType = def.type;
    const trophyLabel = def.label;

    depositBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      if (!lastCube) return;
      if (!hasTrophyInPack(lastCube, trophyType)) {
        notify?.(`No ${trophyLabel} in your backpack`);
        return;
      }
      send("collect-trophy", { trophyType });
    });

    container.appendChild(row);
    trophyRows.push({ el: row, def });
  }

  function refresh(cube: Cube): void {
    lastCube = cube;
    for (const r of trophyRows) {
      const inPack = hasTrophyInPack(cube, r.def.type);
      r.el.classList.toggle("spell-row--unaffordable", !inPack);
    }
  }

  return { el: container, refresh };
}

// ---------------------------------------------------------------------------
// Proximity helper — mirrors tavern-shop's findNearbyTavern.
//
// NOTE: docs/design/17-pets.md cites 6.0u for the lodge interact radius, but
// the server route uses INTERACT_RADIUS = 4 (items/craft.ts). We mirror the
// SERVER value (4) so the E-key guard matches server acceptance.
//
// Kept exported for parity / future use. The E-key handler now uses
// findNearbyCraftBuilding (craft-menu.ts) to match the Lodge, making this
// function unused in main.ts after Task 7 — but it's harmless to keep here.
// ---------------------------------------------------------------------------

export const LODGE_INTERACT_RADIUS = 4;

export function findNearbyLodge(
  cube: Cube,
  buildings: Iterable<{ buildingType: string; teamId: number; state: string; x: number; z: number }>,
  radius: number = LODGE_INTERACT_RADIUS,
): boolean {
  for (const b of buildings) {
    if (b.teamId !== cube.teamId) continue;
    if (b.state !== "ready") continue;
    if (b.buildingType !== "HuntersLodge") continue;
    if (Math.hypot(b.x - cube.x, b.z - cube.z) <= radius) return true;
  }
  return false;
}
